import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Globe, ShieldCheck, Truck } from "@phosphor-icons/react";
import { useLang } from "../i18n/LanguageContext";

export default function Hero() {
  const { t } = useLang();
  return (
    <section data-testid="hero-section" className="relative pt-32 pb-20 md:pt-40 md:pb-28">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 lg:px-14">
        <div className="grid grid-cols-12 gap-6 md:gap-10 mt-2">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="col-span-12 lg:col-span-8"
          >
            <p className="overline-maroon mb-4 md:mb-5">{t("hero.overline")}</p>
            <h1 className="font-serif-display font-light leading-[1.05] tracking-tight text-[#0F1E33] text-[2rem] sm:text-5xl md:text-6xl lg:text-7xl break-words">
              {t("hero.title")}
            </h1>
            <div className="hairline-gold w-24 md:w-32 mt-6 md:mt-8" />
            <p className="mt-6 md:mt-8 max-w-2xl text-[#1F2D40] text-base md:text-lg leading-relaxed">
              {t("hero.body")}
            </p>

            <div className="mt-8 md:mt-10 flex flex-wrap items-center gap-3 md:gap-4">
              <Link
                to="/quote"
                data-testid="hero-quote-cta"
                className="group inline-flex items-center gap-3 px-6 md:px-8 py-3.5 md:py-4 bg-[#6B1F2C] text-[#D4AF37] font-semibold tracking-wide hover:bg-[#4A1620] transition-colors"
              >
                {t("common.requestQuote")}
                <ArrowRight size={18} weight="bold"
                  className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/range"
                data-testid="hero-products-cta"
                className="inline-flex items-center gap-3 px-6 md:px-8 py-3.5 md:py-4 bg-transparent border border-[#B8860B] text-[#1E3A5F] hover:bg-[#B8860B] hover:text-[#D6E2F0] transition-colors tracking-wide"
              >
                {t("common.explore")}
              </Link>
            </div>
          </motion.div>

          {/* Gold-filled Why OMC card */}
          <motion.aside
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="col-span-12 lg:col-span-4"
          >
            <div className="relative bg-[#D4AF37] text-[#0F1E33] p-6 md:p-8 lg:p-10 border border-[#B8860B]">
              <div className="absolute -top-px left-0 w-20 h-px bg-[#1E3A5F]" />
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#1E3A5F] mb-5">
                {t("hero.whyOmc")}
              </div>
              <ul className="space-y-6">
                <Stat icon={<Globe size={22} weight="duotone" />} value={t("hero.benefit1Title")} label={t("hero.benefit1Body")} />
                <Stat icon={<ShieldCheck size={22} weight="duotone" />} value={t("hero.benefit2Title")} label={t("hero.benefit2Body")} />
                <Stat icon={<Truck size={22} weight="duotone" />} value={t("hero.benefit3Title")} label={t("hero.benefit3Body")} />
              </ul>
            </div>
          </motion.aside>
        </div>
      </div>

      {/* Bottom marquee — blue band, white text (homepage hero only) */}
      <div className="mt-20 border-y border-[#0D2E5C] bg-[#0D47A1]">
        <div className="overflow-hidden py-4">
          <div className="marquee-track flex items-center gap-12 whitespace-nowrap text-white text-xs tracking-[0.3em] uppercase font-mono">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="flex items-center gap-12">
                <span>Brake Linings</span><span className="text-[#D4AF37]">·</span>
                <span>Wheel Alignment</span><span className="text-[#D4AF37]">·</span>
                <span>Auto Parts</span><span className="text-[#D4AF37]">·</span>
                <span>OEM Quality</span><span className="text-[#D4AF37]">·</span>
                <span>Heavy Commercial Vehicles</span><span className="text-[#D4AF37]">·</span>
                <span>Light Commercial Vehicles</span><span className="text-[#D4AF37]">·</span>
                <span>Passenger Cars</span><span className="text-[#D4AF37]">·</span>
                <span>UK &amp; Europe Aftermarket</span><span className="text-[#D4AF37]">·</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ icon, value, label }) {
  return (
    <li className="flex items-start gap-4">
      <span className="shrink-0 inline-flex items-center justify-center w-10 h-10 bg-[#D6E2F0] border border-[#1E3A5F]/30 text-[#1E3A5F]">
        {icon}
      </span>
      <div>
        <div className="font-serif-display text-xl leading-tight text-[#0F1E33]">{value}</div>
        <div className="text-sm text-[#15273F] mt-1">{label}</div>
      </div>
    </li>
  );
}
